import { CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useState } from 'react';
import { PaymentCompleted } from '../components/common/PaymentCompleted/PaymentCompleted';
import { PaymentFailure } from '../components/common/PaymentFailure/PaymentFailure';
import { ErrorType, recordError } from '../models/apiWrappers/api/exceptions';
import { useMst } from '../models/rootModel';
import { FinalizePaymentAfterRedirectionParams } from '../models/types/useUserPayment.type';
import { PlanType } from '../types/plan';
import { useAnalytics } from '../utils/analytics';
import { sendGTMEvent } from '../utils/googleTagManager';
import { markConversion } from '../utils/tapfiliate';
import { PaymentSummaryLoaderDiv, PaymentSummaryLoaderText } from './PaymentSummary.styled';
import { calculatePurchasePrice } from '../utils/price/price.utils';

interface PaymentSummaryProps {
  accessToken: string | null;
}

type PaymentSummaryState = 'loading' | 'success' | 'error';

interface MarkConversionParams {
  purchaseId: string;
  promoCode?: string;
  paymentMethod: string;
  planId: string;
  planPrice: number;
  amountAfterDiscount: number;
}

export const PaymentSummary: FC<PaymentSummaryProps> = observer((props) => {
  const {
    UserPayment: { finalizePaymentAfterRedirection },
    UserDiscoveryResponse: { accountId, userPhoneNumber, userName, userEmail },
  } = useMst();
  const analytics = useAnalytics();

  const { accessToken } = props;

  const queryParams = new URLSearchParams(window.location.search);

  const [state, setState] = useState<PaymentSummaryState>('loading');

  const paymentIntentId = queryParams.get('payment_intent');
  const paymentIntentSecret = queryParams.get('payment_intent_client_secret');

  const setupIntentId = queryParams.get('setup_intent');
  const setupIntentSecret = queryParams.get('setup_intent_client_secret');
  const redirectStatus = queryParams.get('redirect_status');
  const planType = queryParams.get('plan_type') as PlanType | null;
  const planId = queryParams.get('plan_id');
  const isPaymentAfterMagicDoor = queryParams.get('md');

  const markUserConversion = useCallback(
    async (params: MarkConversionParams) => {
      const currency = 'USD';
      sendGTMEvent({
        event: 'purchase',
        user_id: accountId,
        phone: userPhoneNumber,
        full_name: userName,
        email: userEmail,
        ecommerce: {
          transaction_id: params.purchaseId,
          value: calculatePurchasePrice(params.amountAfterDiscount),
          currency,
          tax: 0,
          coupon: params.promoCode,
          first_time_client: 'yes',
          transaction_method: params.paymentMethod,
          user_id: accountId,
          items: [
            {
              item_id: params.planId,
              item_name: 'Premium',
              item_brand: 'Circles',
              coupon: params.planId,
              currency,
              discount: params.amountAfterDiscount,
              price: params.planPrice,
              quantity: 1,
            },
          ],
        },
      });

      sendGTMEvent({
        event: 'awin.dl.ready',
        transactionTotal: calculatePurchasePrice(params.amountAfterDiscount),
        transactionCurrency: currency,
        transactionID: params.purchaseId,
        transactionPromoCode: params.promoCode,
      });

      markConversion(accessToken ?? 'unknown', {
        amountInCents: params.amountAfterDiscount,
        currencyIsoCode: currency,
      });
    },
    [accountId, accessToken, userEmail, userName, userPhoneNumber]
  );

  useEffect(() => {
    const processPaymentIfNeeded = async () => {
      try {
        if (isPaymentAfterMagicDoor) {
          setState('success');
          return;
        }

        if (!accountId) {
          return;
        }

        if (!redirectStatus) {
          setState('error');
          return;
        }

        const isPaymentIntent = paymentIntentId && paymentIntentSecret;
        const isSetupIntent = setupIntentId && setupIntentSecret;

        if (!isPaymentIntent && !isSetupIntent) {
          setState('error');
          return;
        }

        if (isPaymentIntent && isSetupIntent) {
          setState('error');
          return;
        }

        if (redirectStatus !== 'succeeded') {
          analytics.track(`view payment error`, {
            error: 'unsuccesful payment after redirection',
            produckSku: planId ?? 'unknown',
          });

          setState('error');
          return;
        }

        if (!accessToken) {
          return;
        }

        let params: FinalizePaymentAfterRedirectionParams | null = null;

        if (isPaymentIntent) {
          params = {
            paymentIntentId,
            paymentIntentClientSecret: paymentIntentSecret,
          };
        } else {
          params = {
            setupIntentId: setupIntentId!,
            setupIntentClientSecret: setupIntentSecret!,
          };
        }

        setState('loading');
        const requestResult = await finalizePaymentAfterRedirection(params);

        if (requestResult.success || requestResult.paymentAlreadyExists) {
          setState('success');
        } else {
          setState('error');
        }

        if (requestResult.success) {
          markUserConversion({
            amountAfterDiscount: requestResult.data.amountAfterDiscount,
            paymentMethod: requestResult.data.paymentMethod,
            planId: requestResult.data.plan.id,
            planPrice: requestResult.data.plan.price,
            purchaseId: requestResult.data.purchaseId,
            promoCode: requestResult.data.usedPromoCode,
          });
        }
      } catch (error: any) {
        recordError(ErrorType.HANDLED, error);
        analytics.track(`view payment error`, {
          error: error?.message ?? 'unexpected error',
          produckSku: planId ?? 'unknown',
        });
        setState('error');
      }
    };

    processPaymentIfNeeded();
  }, [
    finalizePaymentAfterRedirection,
    accountId,
    paymentIntentId,
    paymentIntentSecret,
    setupIntentId,
    setupIntentSecret,
    redirectStatus,
    accessToken,
    markUserConversion,
    analytics,
    planId,
    isPaymentAfterMagicDoor,
  ]);

  return (
    <>
      {state === 'loading' && (
        <PaymentSummaryLoaderDiv>
          <PaymentSummaryLoaderText>
            We are now processing your payment, don't close the browser window
          </PaymentSummaryLoaderText>
          <CircularProgress />
        </PaymentSummaryLoaderDiv>
      )}
      {state === 'success' && <PaymentCompleted />}
      {state === 'error' && <PaymentFailure planType={planType} />}
    </>
  );
});
